/**
 * The claims an independent claim's framework row brings to the writer: every claim beneath it.
 *
 * The framework places independent claims only; each brings its dependents, and the writer
 * supports each of them in the section that holds the row. The row says which they are.
 */

interface NumberedClaim {
  claim_id: string;
  number: number;
  parent_claim_id: string | null;
}

/** The numbers of every claim beneath the claim named, however deep, in number order. */
export function claimsBeneath(
  claims: readonly NumberedClaim[],
  claimId: string,
): number[] {
  const found: number[] = [];
  const frontier = [claimId];
  const seen = new Set(frontier);
  while (frontier.length > 0) {
    const at = frontier.pop();
    for (const claim of claims) {
      if (claim.parent_claim_id === at && !seen.has(claim.claim_id)) {
        seen.add(claim.claim_id);
        found.push(claim.number);
        frontier.push(claim.claim_id);
      }
    }
  }
  return found.sort((a, b) => a - b);
}

/** "with claims 2 to 7", "with claims 2, 4 and 6", "with claim 6"; nothing when there are none. */
export function withClaimsWords(
  numbers: readonly number[],
): string | undefined {
  if (numbers.length === 0) return undefined;
  if (numbers.length === 1) return `with claim ${String(numbers[0])}`;
  const first = numbers[0];
  const last = numbers[numbers.length - 1];
  const run = numbers.every((n, at) => n === first + at);
  if (run && numbers.length > 2)
    return `with claims ${String(first)} to ${String(last)}`;
  const head = numbers.slice(0, -1).map(String).join(", ");
  return `with claims ${head} and ${String(last)}`;
}
